import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { GENDER_OPTIONS } from "../../../constants/GenderOptions";
import { useTheme } from "../../../context/ThemeContext";

function RegistrationPersonalInfo({
  firstName, lastName, email, password, confirmPassword,
  setFirstName, setLastName, setEmail, setPassword, setConfirmPassword,
  role, birthDate, setBirthDate,
  receiveNewsletter, setReceiveNewsletter,
  agreeToTerms, setAgreeToTerms,
  gender, setGender,
}) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const inputClass = isLight
    ? "tw-w-full tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-px-4 tw-py-3 tw-text-base tw-text-[#1E0039] tw-placeholder-[#9ca3af] tw-outline-none tw-transition-colors focus:tw-border-[#FF43A7]/60"
    : "tw-w-full tw-rounded-lg tw-border tw-border-white/10 tw-bg-white/5 tw-px-4 tw-py-3 tw-text-base tw-text-white tw-placeholder-gray-500 tw-outline-none tw-transition-colors focus:tw-border-[#FF43A7]/60";

  return (
    <div className="tw-flex tw-flex-col">
      <div className="tw-mb-6">
        <h2 className={`tw-text-2xl tw-font-bold ${isLight ? 'tw-text-[#1E0039]' : 'tw-text-white'}`}>{t("Create your account")}</h2>
        {role?.value && (
          <div className="tw-mt-2 tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-[#FF43A7]/40 tw-bg-[#FF43A7]/10 tw-px-3 tw-py-1">
            {role.icon && (
              <FontAwesomeIcon icon={role.icon} className="tw-text-xs tw-text-[#FF43A7]" />
            )}
            <span className="tw-text-xs tw-font-semibold tw-text-[#FF43A7]">{t(role.value)}</span>
          </div>
        )}
      </div>

      <div className="tw-flex tw-flex-col tw-gap-3">
        <div className="tw-flex tw-gap-3">
          <input
            className={inputClass}
            type="text"
            id="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder={t("First Name")}
          />
          <input
            className={inputClass}
            type="text"
            id="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder={t("Last Name")}
          />
        </div>

        <input
          className={inputClass}
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("Email")}
        />

        {role.value === "Actor" && (
          <select
            id="gender"
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            className={`${inputClass} ${
              !gender
                ? (isLight ? 'tw-text-gray-400' : 'tw-text-gray-500')
                : (isLight ? 'tw-text-[#1E0039]' : 'tw-text-white')
            } ${isLight ? '' : 'tw-bg-[#1a0a2e]'}`}
          >
            <option value="" disabled hidden style={{ backgroundColor: isLight ? 'white' : '#1a0a2e', color: isLight ? '#9ca3af' : '#6b7280' }}>{t("Playing Gender")}</option>
            {GENDER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value} style={{ backgroundColor: isLight ? 'white' : '#1a0a2e', color: isLight ? '#1E0039' : 'white' }}>{t(opt.label)}</option>
            ))}
          </select>
        )}

        <div>
          <label className="tw-mb-1 tw-block tw-text-xs tw-font-medium tw-text-gray-400">
            {t("Date of Birth")}
          </label>
          <input
            className={inputClass}
            type="date"
            id="birthDate"
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
          />
        </div>

        <div className="tw-relative">
          <input
            className={`${inputClass} tw-pr-12`}
            type={showPassword ? "text" : "password"}
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("Password")}
          />
          <button
            type="button"
            onClick={() => setShowPassword((p) => !p)}
            className={`tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-w-12 tw-cursor-pointer tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-transition-colors ${
              isLight ? 'tw-text-gray-400 hover:tw-text-[#1E0039]' : 'tw-text-gray-500 hover:tw-text-gray-300'
            }`}
          >
            <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
          </button>
        </div>

        <div className="tw-relative">
          <input
            className={`${inputClass} tw-pr-12`}
            type={showConfirm ? "text" : "password"}
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={t("Confirm Password")}
          />
          <button
            type="button"
            onClick={() => setShowConfirm((p) => !p)}
            className={`tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-w-12 tw-cursor-pointer tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-transition-colors ${
              isLight ? 'tw-text-gray-400 hover:tw-text-[#1E0039]' : 'tw-text-gray-500 hover:tw-text-gray-300'
            }`}
          >
            <FontAwesomeIcon icon={showConfirm ? faEyeSlash : faEye} />
          </button>
        </div>

        <label className="tw-flex tw-cursor-pointer tw-items-start tw-gap-3 tw-pt-1">
          <input
            type="checkbox"
            checked={agreeToTerms}
            onChange={() => setAgreeToTerms(!agreeToTerms)}
            className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-[#FF43A7] focus:tw-ring-[#FF43A7]"
          />
          <span className={`tw-text-xs ${isLight ? 'tw-text-gray-500' : 'tw-text-gray-400'}`}>
            {t("I agree to the terms and conditions as set out by the user agreement")}
          </span>
        </label>

        <label className="tw-flex tw-cursor-pointer tw-items-start tw-gap-3">
          <input
            type="checkbox"
            checked={receiveNewsletter}
            onChange={(e) => setReceiveNewsletter(e.target.checked)}
            className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-[#FF43A7] focus:tw-ring-[#FF43A7]"
          />
          <span className={`tw-text-xs ${isLight ? 'tw-text-gray-500' : 'tw-text-gray-400'}`}>
            {t("I want to stay up to date and receive newsletters from KinoKlik!")}
          </span>
        </label>
      </div>
    </div>
  );
}

export default RegistrationPersonalInfo;
